import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SocialLoginFragment from '@app/site/authentication/fragment/SocialRegistrationFragment';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import SecurityService from '@app/model/_security/service/SecurityService';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from '@app/component/translation/Translation';
import useAppForm from '@app/site/_form';
import * as z from 'zod/v4';

const SignInPage = React.memo(() => {

	const toaster = useToaster();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [isSocialSubmitting, setIsSocialSubmitting] = useState<boolean>();

	const SignInFormValidation = z.object({
		username: z.string().nonempty('Username is required'),
		password: z.string().nonempty('Password is required'),
		rememberMe: z.boolean(),
	});

	const form = useAppForm({
		defaultValues: {
			username: '',
			password: '',
			rememberMe: false,
		},
		validators: {
			onChange: SignInFormValidation,
		},
		onSubmit: async ({ value }) => signInService.mutateAsync(value).catch(() => {
		}),
	});


	const signInService = useMutation({
		mutationFn: SecurityService.signIn,
		onSuccess: (principalWithToken) => {
			SecurityService.login(principalWithToken);
			navigate('/oauth2/redirect');
		},
		onError: (error: string) => {
			toaster.error('Sign in to your account', t(error));
		},
	});

	return <React.Fragment>
		<div className="min-h-full bg-white">
			<div className="flex lg:h-dvh min-w-max content-center justify-center flex-1">
				<div className="flex flex-1 flex-col justify-center px-6 py-12">
					<div className="mx-auto w-full max-w-sm lg:w-96">
						<div>
							<a href="http://roompulse.io">
								<img
									className="h-10 w-auto"
									src="/assets/img/brand/logo-icon.png"
									alt="RoomPulse" />
							</a>
							<h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
								Sign in to your account
							</h2>
							<p className="mt-2 text-sm leading-6 text-gray-500">
								Don't have an account yet?{' '}
								<Link to="/sign-up" className="font-semibold text-blue-600 hover:text-blue-500"
											title="Click to create a new account"> Create a new account</Link>
							</p>
						</div>

						<form.AppForm>
							<div className="mt-10">
								<div>
									<form className="space-y-6" onSubmit={(e) => {
										e.preventDefault();
										e.stopPropagation();
									}}>
										<form.AppField name="username"
																	 children={(field) => <field.TextField label="Email address" />} />
										<form.AppField name="password"
																	 children={(field) => <field.PasswordField label="Password" />} />

										<div className="text-sm">
											<Link to="/forgot-password"
														className="font-medium text-blue-600 hover:text-blue-500">
												Forgot your password?
											</Link>
										</div>

										<div>
											<form.SubmitButton state="primary" extraClassName="w-full" disabled={isSocialSubmitting}>
												Sign in
											</form.SubmitButton>
										</div>
									</form>
								</div>

								<div className="mt-10">
									<SocialLoginFragment onPending={setIsSocialSubmitting} />
								</div>
							</div>
						</form.AppForm>
					</div>
				</div>
				<div className="relative hidden flex-1 lg:block">
					<img
						className="absolute inset-0 h-full w-full object-cover"
						src="https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?q=80&w=2574&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
						alt=""
					/>
				</div>
			</div>
		</div>
	</React.Fragment>;
});

export default SignInPage;